import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import {
  SelfOnboardingStepKey,
  Jobsite,
  SelfOnboardingNavigation,
  SelfOnboardingLocationState,
  SelfOnboardingState,
  SelfOnboardingLocationSearch,
  GoToStepOptions,
  SelfOnboardingNavigationStepKey,
} from 'containers/selfOnboarding/types';
import {
  defaultStep,
  getBaseRoute,
  resolveSelfOnboardingStep,
} from 'containers/selfOnboarding/utils';
import { DocumentKey } from 'containers/worker/utils';
import { isStringEnumValue } from 'utils';
import {
  useCompleteSelfOnBoardingMutation,
  useUpdateWorkerMutation,
} from 'apollo/generated/client-operations';
import { getVisibleSequentialStepsFrom } from './utils';
import {
  authenticatedLastStep,
  authenticatedNoGoBackSteps,
  authenticatedNoOrderInfoSteps,
  authenticatedPreviousSteps,
  authenticatedSequentialSteps,
  authenticatedWhiteListedSteps,
} from './authenticatedSelfOnboardingNavigation.config';

type UseAuthenticatedSelfOnboardingNavigationArgs = {
  selfOnboardingState: SelfOnboardingState;
  jobsite: Jobsite;
};

/**
 * Navigation brain for the *authenticated* self-onboarding flow. This is the
 * forked, isolated counterpart to {@link useSelfOnboardingNavigation}: it reads
 * no feature flag (the route entry already selected this flow) and drives the
 * new-flow step order/whitelist from
 * {@link ./authenticatedSelfOnboardingNavigation.config}.
 */
export function useAuthenticatedSelfOnboardingNavigation(
  args: UseAuthenticatedSelfOnboardingNavigationArgs,
): SelfOnboardingNavigation {
  const { selfOnboardingState, jobsite } = args;
  const { worker, jobsiteWorker, shouldSelectContractor } =
    selfOnboardingState ?? {};
  const { workerId, selfOnboardingCurrentStep, isAssignedToMultipleJobsites } =
    worker ?? {};
  const { jobsiteWorkerId } = jobsiteWorker ?? {};
  const isSelfOnboardingClosedOrCompleted =
    selfOnboardingCurrentStep === SelfOnboardingStepKey.Closed ||
    selfOnboardingCurrentStep === authenticatedLastStep;
  const history = useHistory();
  const location = useLocation<SelfOnboardingLocationState>();
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [isReview, setIsReview] = React.useState(false);
  const [updateWorker] = useUpdateWorkerMutation();
  const [completeSelfOnBoarding] = useCompleteSelfOnBoardingMutation();

  const locationState = location.state ?? {};
  const { jobsiteInvitationId, step: urlStep } = useParams<{
    jobsiteInvitationId: string;
    step: string;
  }>();
  const baseRoute = getBaseRoute(jobsiteInvitationId);

  const locationSearchRef = React.useRef(location.search);
  const updateQueryParams = (
    queryParams?: SelfOnboardingLocationSearch,
  ): void => {
    locationSearchRef.current = `?${queryString.stringify(queryParams, {
      skipEmptyString: true,
      skipNull: true,
    })}`;
  };

  const confirmInitialization = (): void => {
    if (!isInitialized) {
      setIsInitialized(true);
    }
  };

  const goToStep = React.useCallback<SelfOnboardingNavigation['goToStep']>(
    (step: SelfOnboardingNavigationStepKey, options?: GoToStepOptions) => {
      const { tab, state, shouldReplace } = options ?? {};
      const pathname = [baseRoute, step === defaultStep ? '' : step, tab]
        .filter(Boolean)
        .join('/');
      const goTo = shouldReplace ? history.replace : history.push;
      goTo({ pathname, state, search: locationSearchRef.current });

      if (step === authenticatedLastStep && !isReview) {
        setIsReview(true);
      }
    },
    [history, baseRoute, isReview],
  );

  const { visibleSequentialSteps, visibleSequentialStepKeys } =
    React.useMemo(() => {
      const vss = getVisibleSequentialStepsFrom(authenticatedSequentialSteps, {
        ...selfOnboardingState,
        jobsite,
      });
      return {
        visibleSequentialSteps: vss,
        visibleSequentialStepKeys: vss.map((s) =>
          typeof s === 'string' ? s : s.key,
        ),
      };
    }, [jobsite, isAssignedToMultipleJobsites, shouldSelectContractor]);

  const getStepIndex = React.useCallback<
    SelfOnboardingNavigation['getStepIndex']
  >(
    (step) => visibleSequentialStepKeys.findIndex((s) => s === step),
    [visibleSequentialStepKeys],
  );

  const selfOnboardingStepInfo = resolveSelfOnboardingStep(
    urlStep,
    visibleSequentialSteps,
  );
  const { currentStep, currentConfigStep } = selfOnboardingStepInfo;

  const workerWillBeAssigned = !jobsiteWorker;

  // Enforce the contractor-selection step. It is the Mutation B binding point
  // every worker passes through — where the jobsiteWorker is created, whether
  // or not a contractor picker is shown (auto-assign jobsites bind there too).
  // The whitelist-bounce effect below only redirects UNauthenticated visitors
  // (worker === null), so once signed in a worker could hard-navigate via the
  // URL straight to a later step and reach jobsite-data/documents with no
  // jobsiteWorker. Send a signed-in, not-yet-bound worker who has landed on a
  // sequential step *after* contractor selection back to it. Keyed on the
  // absence of a jobsiteWorker (not `shouldSelectContractor`) so it covers
  // auto-assign jobsites too. Gated on a session worker so it never fires
  // pre-OTP (which the phone-verified whitelist would then bounce back to
  // language), and scoped to sequential steps (index >= 0) so the abort/help
  // screens (JobsiteWorkerWithDifferentContractor, AlreadyCompleted, etc. —
  // index -1) are left untouched.
  const contractorSelectionStepIndex = getStepIndex(
    SelfOnboardingStepKey.ContractorSelection,
  );
  const shouldGoToContractorSelectionStep = Boolean(
    worker &&
      !jobsiteWorker &&
      contractorSelectionStepIndex >= 0 &&
      getStepIndex(currentStep) > contractorSelectionStepIndex,
  );

  // Redirect to the persisted step (fast-forward) or to `defaultStep`.
  React.useEffect(() => {
    // `selfOnboardingCurrentStep` is worker-level, not jobsiteWorker-level: an
    // existing worker who closed self-onboarding on another jobsite carries
    // `Closed` here. Only honor it when a jobsiteWorker exists on THIS jobsite —
    // otherwise let them onboard via contractor-selection instead of bouncing
    // them to the closed page (sc-16835).
    if (
      jobsiteWorker &&
      selfOnboardingCurrentStep === SelfOnboardingStepKey.Closed
    ) {
      if (currentStep !== SelfOnboardingStepKey.Closed) {
        goToStep(SelfOnboardingStepKey.Closed);
      }
      confirmInitialization();
      return;
    }

    // Before the jobsiteWorker exists there is no persisted step to resume to;
    // let the whitelist-bounce effect below settle the position.
    if (workerWillBeAssigned) {
      confirmInitialization();
    } else if (
      jobsiteWorker &&
      isStringEnumValue(SelfOnboardingStepKey, selfOnboardingCurrentStep)
    ) {
      const currentStepIndex = getStepIndex(currentStep);
      const selfOnboardingCurrentStepIndex = getStepIndex(
        selfOnboardingCurrentStep,
      );

      const canGoToPersistedCurrentStep =
        selfOnboardingCurrentStepIndex >= 0 ||
        isSelfOnboardingClosedOrCompleted;
      if (canGoToPersistedCurrentStep) {
        const shouldGoToPersistedCurrentStep =
          currentStep === defaultStep ||
          isSelfOnboardingClosedOrCompleted ||
          currentStepIndex === -1 ||
          selfOnboardingCurrentStepIndex < currentStepIndex;

        if (shouldGoToPersistedCurrentStep) {
          goToStep(selfOnboardingCurrentStep);
        }
        confirmInitialization();
      } else if (currentStep !== defaultStep) {
        goToStep(defaultStep);
        confirmInitialization();
      }
    }
  }, [selfOnboardingCurrentStep]);

  const isCurrentStepWhiteListed = (): boolean => {
    return authenticatedWhiteListedSteps.some((step) => {
      return typeof step === 'string'
        ? step === currentStep
        : step.key === currentStep && step.isWhiteListed(selfOnboardingState);
    });
  };

  // Bounce an unauthenticated visitor (no session worker) off any non-whitelisted
  // step back to the start.
  React.useEffect(() => {
    if (currentStep !== defaultStep || selfOnboardingCurrentStep) {
      if (
        worker === null && // session query completed with no current worker
        !isCurrentStepWhiteListed()
      ) {
        goToStep(defaultStep);
      }
    }
    if (
      worker !== undefined && // session query completed
      (!isStringEnumValue(SelfOnboardingStepKey, selfOnboardingCurrentStep) ||
        currentStep === selfOnboardingCurrentStep)
    ) {
      confirmInitialization();
    }
  }, [currentStep, worker]);

  // Send a hard-navigated worker who skipped past contractor selection back to
  // it. Keyed on the guard itself so it fires once the session state resolves
  // after a hard navigation, not only on the initial step/worker transition.
  React.useEffect(() => {
    if (shouldGoToContractorSelectionStep) {
      goToStep(SelfOnboardingStepKey.ContractorSelection);
    }
  }, [shouldGoToContractorSelectionStep]);

  const goToLastStep = React.useCallback<
    SelfOnboardingNavigation['goToLastStep']
  >(
    async (tab) => {
      goToStep(authenticatedLastStep, { tab });
      if (workerId) {
        await updateWorker({
          variables: {
            workerId,
            workerInput: { selfOnboardingCurrentStep: authenticatedLastStep },
          },
        });
        await completeSelfOnBoarding({ variables: { jobsiteWorkerId } });
      }
    },
    [goToStep, workerId, jobsiteWorkerId],
  );

  const getNextStep = React.useCallback<
    SelfOnboardingNavigation['getNextStep']
  >(() => {
    const currentStepIndex = getStepIndex(currentStep);
    return visibleSequentialStepKeys[currentStepIndex + 1];
  }, [visibleSequentialStepKeys, currentStep, getStepIndex]);

  const goToNextStep = React.useCallback<
    SelfOnboardingNavigation['goToNextStep']
  >(async () => {
    const currentStepIndex = getStepIndex(currentStep);
    const nextStep = isReview
      ? null
      : visibleSequentialStepKeys[currentStepIndex + 1];

    goToStep(
      nextStep ?? authenticatedLastStep,
      isReview && isStringEnumValue(DocumentKey, currentConfigStep)
        ? { tab: 'documents' }
        : null,
    );

    if (workerId && nextStep) {
      await updateWorker({
        variables: {
          workerId,
          workerInput: { selfOnboardingCurrentStep: nextStep },
        },
      });
      if (nextStep === authenticatedLastStep) {
        await completeSelfOnBoarding({ variables: { jobsiteWorkerId } });
      }
    }
  }, [
    workerId,
    jobsiteWorkerId,
    isReview,
    currentStep,
    getStepIndex,
    goToStep,
    visibleSequentialStepKeys,
  ]);

  const goToPreviousStep = React.useCallback(() => {
    const currentStepIndex = getStepIndex(currentStep);
    const prevStep =
      authenticatedPreviousSteps[currentStep as SelfOnboardingStepKey] ??
      visibleSequentialStepKeys[currentStepIndex - 1] ??
      defaultStep;
    goToStep(prevStep);
  }, [currentStep, getStepIndex, goToStep, visibleSequentialStepKeys]);

  const getStepNumberInfo = (): { stepsCount: number; currentStep: number } => {
    const orderedSteps = visibleSequentialStepKeys.filter(
      (step) =>
        !authenticatedNoOrderInfoSteps.includes(step as SelfOnboardingStepKey),
    );
    const currentStepIndex = orderedSteps.findIndex(
      (step) => step === currentStep,
    );
    if (currentStepIndex >= 0) {
      return {
        stepsCount: orderedSteps.length,
        currentStep: currentStepIndex + 1,
      };
    }
    return null;
  };

  const canGoToPreviousStep: SelfOnboardingNavigation['canGoToPreviousStep'] =
    React.useCallback(
      (step) =>
        !authenticatedNoGoBackSteps.includes(step as SelfOnboardingStepKey),
      [],
    );

  return React.useMemo<SelfOnboardingNavigation>(
    () => ({
      loading: !isInitialized,
      currentStep,
      currentConfigStep,
      state: { ...locationState, isReview },
      goToStep,
      goToNextStep,
      canGoToPreviousStep,
      goToPreviousStep,
      goToLastStep,
      getStepNumberInfo,
      getStepIndex,
      getNextStep,
      updateQueryParams,
    }),
    [
      isInitialized,
      currentStep,
      currentConfigStep,
      locationState,
      isReview,
      goToStep,
      goToNextStep,
      canGoToPreviousStep,
      goToPreviousStep,
      goToLastStep,
      getStepNumberInfo,
      getStepIndex,
      getNextStep,
    ],
  );
}
